/**
 * Result export (ADR-038 Phase I; REQ-UI-127).
 *
 * Two items in the table-settings cog that download the current result set
 * as CSV or JSON-Lines.
 *
 * # Why this fetches and then downloads, rather than navigating
 *
 * The export carries the same predicate document the records query does —
 * a GET would spill operator-authored filter values into access logs and
 * browser history (D3) — so it is a POST. A POST cannot be a plain
 * navigation, and a hidden form POST cannot set the ``X-Meridian-CSRF``
 * header the BFF requires on every mutating request; smuggling the
 * sentinel through a body field instead would mean weakening that check
 * for one route. So the file is fetched through the shared client (which
 * sets the header and carries the session cookie) and handed to the
 * browser as an object URL.
 *
 * The response is bounded by the server's 10 000-record export cap, so
 * holding it in memory briefly is not the unbounded-buffering hazard the
 * raw-blob download route avoids — that one is a streamed GET precisely
 * because a blob has no such cap.
 *
 * # Over-cap is the server's verdict, shown verbatim
 *
 * The BFF rejects a result larger than its cap rather than truncating
 * (D20) — a silently short export is a wrong answer the operator cannot
 * detect. The UI does not pre-judge that; it surfaces the server's own
 * message, which also covers the case where the result grew between the
 * last query and the export.
 *
 * Cross-references:
 *   - REQ-UI-127; REQ-QUERY-046 / REQ-QUERY-047.
 *   - REQ-UI-035 (same-origin only), REQ-UI-037 (no internal-error leakage).
 */
import { useState } from "react";
import { Export } from "@phosphor-icons/react";
import { apiFetch } from "@/lib/api-client";
import { exportErrorMessage } from "@/query/query-export-error";

/** Formats the export route renders. */
export type ExportFormat = "csv" | "jsonl";

export interface QueryExportActionProps {
  /** The records request body, exactly as the panel would query it. */
  readonly body: Record<string, unknown> | null;
  /** The columns to project — the operator's visible set. */
  readonly columns: readonly string[];
  /** The data type, for the downloaded file's name. */
  readonly typeId: string;
  readonly testId?: string;
}

/**
 * Render the export items.
 *
 * Disabled when there is no runnable query: offering an export of an
 * invalid or half-typed query would send a request the BFF is going to
 * reject — the silent dead end CLAUDE.md §8.1 rules out.
 */
export function QueryExportAction({
  body,
  columns,
  typeId,
  testId = "query-export",
}: QueryExportActionProps): React.ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (format: ExportFormat): Promise<void> => {
    if (body === null) return;
    setBusy(true);
    setError(null);
    try {
      const response = await apiFetch<Response>(`/api/query/records/export?format=${format}`, {
        method: "POST",
        body: JSON.stringify({ ...body, columns: [...columns] }),
        // The route answers with a file, not JSON.
        rawResponse: true,
      });
      const blob = await response.blob();
      triggerDownload(blob, `${typeId}.${format}`);
    } catch (caught) {
      setError(exportErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const itemClass =
    "flex w-full items-center gap-2 px-4 py-2.5 text-body-sm text-text-primary hover:bg-bg-elevated focus:bg-bg-elevated focus:outline-none disabled:opacity-40";

  return (
    <>
      <button
        type="button"
        onClick={() => void run("csv")}
        disabled={body === null || busy}
        data-testid={`${testId}-csv`}
        className={itemClass}
      >
        <Export size={14} aria-hidden="true" />
        Export results as CSV
      </button>
      <button
        type="button"
        onClick={() => void run("jsonl")}
        disabled={body === null || busy}
        data-testid={`${testId}-jsonl`}
        className={itemClass}
      >
        <Export size={14} aria-hidden="true" />
        Export results as JSON-Lines
      </button>
      {error !== null && (
        <p
          role="alert"
          className="px-4 pb-2 text-meta text-destructive"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </>
  );
}

/** Hand a fetched file to the browser's own download handling. */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Release the object URL once the click has been dispatched; holding it
  // would pin the whole export in memory for the life of the document.
  URL.revokeObjectURL(url);
}
